import { fetchAuthSession } from 'aws-amplify/auth';
import FormImageCropper from 'components/form/FormImageCropper';
import FormSelect from 'components/form/FormSelect';
import FormText from 'components/form/FormText';
import FormWysiwygEditor from 'components/form/FormWysiwygEditor';
import IndicatorButton from 'components/indicator/IndicatorButton';
import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import mime from 'mime';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';

/**
 * アカウント登録カンパニー情報入力画面
 * 2025/05/09 トライアルプラン改修で新しい画面を追加したため利用されていない
 */
export default function SignupCompany(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type FormStateType = {
    companyName: string;
    companyTel: string;
    zipCode: string;
    prefectureDiv: number;
    city: string;
    address: string;
    buildingName: string;
    reportMailSenderName: string;
    reportMailSenderAddress: string;
    reportMailReplyAddress: string;
    reportMailSignatureHtml: string;
    privacyPolicyUrl: string;
    copyright?: string;
    apiKeyBenchmark?: string;
    apiKeyGoogleAnalytics?: string;
    userName: string;
  };

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const [acctState] = useRecoilState(AcctState);

  const { initializeAcctState } = useAppState();
  const { postCompanyRegister } = useApiState();
  const [processing, setProcessing] = useState(false);
  const [isImageCropperOpenState, setIsImageCropperOpenState] = useState(false);
  const [isImageCropperResetState, setIsImageCropperResetState] = useState(false);
  const [imageBlobState, setImageBlobState] = useState<Blob>(null);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<FormStateType>();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    // フォームの初期化
    setValue('reportMailSenderAddress', config.ses.from.address);
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　登録処理
   */
  const onSave = () =>
    AppHelper.submit(
      processing,
      setProcessing,
      handleSubmit(async (data: FormStateType) => {
        try {
          // Cognito認証情報の取得
          const session = await fetchAuthSession();
          // カンパニー登録APIの呼び出し
          const response = await postCompanyRegister({
            company: {
              name: data.companyName,
              tel: data.companyTel,
              zipCode: data.zipCode,
              prefectureDiv: data.prefectureDiv,
              city: data.city,
              address: data.address,
              buildingName: data.buildingName,
              imageUploadKey: imageBlobState
                ? await AppHelper.uploadS3(imageBlobState, mime.getExtension(imageBlobState.type))
                : null,
              reportMailSenderName: data.reportMailSenderName,
              reportMailReplyAddress: data.reportMailReplyAddress,
              reportMailSignatureHtml: data.reportMailSignatureHtml,
              privacyPolicyUrl: data.privacyPolicyUrl,
              copyright: data.copyright,
              termsInputDiv: DivConst.items.termsInputType.url.value,
              termsUrl: null,
              termsText: null,
              apiKeyBenchmark: data.apiKeyBenchmark,
              apiKeyGoogleAnalytics: data.apiKeyGoogleAnalytics,
            },
            user: acctState.user
              ? null
              : {
                  accessToken: session.tokens.accessToken.toString(),
                  name: data.userName,
                  loginRouteDiv: DivConst.items.loginRoute.mail.value,
                },
          });

          // APIのエラー処理
          if (response.error?.duplicated) {
            setError('companyName', {
              type: 'custom',
              message: 'この名前はすでに登録されています。',
            });
            return;
          }
          // アカウント情報を初期化
          await initializeAcctState(response.companyCd);
          // ホーム画面に遷移
          navigate('/');
        } catch (e) {
          showBoundary(e);
        }
      })
    );

  /**
   *　画像アップロードボタンクリック時の処理
   */
  const onImageUpload = () => {
    setIsImageCropperOpenState(true);
  };

  /**
   *　画像削除ボタンクリック時の処理
   */
  const onImageDelete = () => {
    setIsImageCropperResetState(true);
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-header mt-5">
        <div className="container">
          <div className="d-md-flex d-flex-header">
            <div className="flex-shrink-0 flex-left">
              <h2 className="flex-title">アカウントの登録</h2>
            </div>
          </div>
        </div>
      </section>
      <section className="section-basic-setting">
        <div className="container">
          <div className="box-common">
            <form className="form-profile g-3">
              <div className="form-about-us">
                <div className="d-md-flex d-flex-header">
                  <div className="flex-shrink-0 flex-left mb-3">
                    <h3 className="title-section title-md fw-bold">会社情報</h3>
                  </div>
                </div>{' '}
                <div className="d-flex justify-content-start align-items-center">
                  <div className="flex-shrink-0 flex-img me-4">
                    <FormImageCropper
                      isOpen={isImageCropperOpenState}
                      setIsOpen={setIsImageCropperOpenState}
                      isReset={isImageCropperResetState}
                      setIsReset={setIsImageCropperResetState}
                      imageUrl={AppHelper.getCompanyImageUrl(null)}
                      imageUrlDefault={AppHelper.getCompanyImageUrl(null)}
                      cropWidth={256}
                      cropRatio={1}
                      displayWidth={80}
                      setImageBlob={setImageBlobState}
                    />
                  </div>

                  <div className="flex-grow-1 flex-text mb-0 ps-1">
                    <a onClick={onImageUpload} className="mb-2">
                      画像アップロード
                    </a>
                    <br />
                    <a onClick={onImageDelete}>削除</a>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="column col-lg-6 col-xl-6 col-left">
                    <FormText
                      subject="法人名"
                      name="companyName"
                      placeholder="株式会社サンプル"
                      register={register}
                      errors={errors}
                      required={true}
                      maxLength={100}
                      requiredMark={true}
                    />
                  </div>
                  <div className="column col-lg-6 col-xl-6 col-right">
                    <FormText
                      subject="電話番号"
                      name="companyTel"
                      placeholder="1234567890"
                      register={register}
                      errors={errors}
                      required={true}
                      maxLength={50}
                      format="numeric"
                      requiredMark={true}
                    />
                  </div>
                </div>
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="column col-lg-6 col-xl-6 col-left">
                    <FormText
                      subject="郵便番号"
                      name="zipCode"
                      placeholder="1000001"
                      register={register}
                      errors={errors}
                      required={true}
                      maxLength={7}
                      minLength={7}
                      format="numeric"
                      requiredMark={true}
                    />
                  </div>
                  <div className="column col-lg-6 col-xl-6 col-right">
                    <FormSelect
                      subject="都道府県"
                      name="prefectureDiv"
                      register={register}
                      errors={errors}
                      options={AppHelper.getDivSelectOptions('prefecture')}
                      required={true}
                      blank={true}
                      blankLabel="選択してください"
                      requiredMark={true}
                    />
                  </div>
                </div>
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="column col-lg-6 col-xl-6 col-left">
                    <FormText
                      subject="市区町村"
                      name="city"
                      placeholder="千代田区"
                      register={register}
                      errors={errors}
                      required={true}
                      maxLength={100}
                      requiredMark={true}
                    />
                  </div>
                  <div className="column col-lg-6 col-xl-6 col-right">
                    <FormText
                      subject="番地以降"
                      name="address"
                      placeholder="丸の内1-1-1"
                      register={register}
                      errors={errors}
                      required={true}
                      maxLength={100}
                      requiredMark={true}
                    />
                  </div>
                </div>
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="column col-lg-6 col-xl-6 col-left">
                    <FormText
                      subject="建物名・部屋番号"
                      name="buildingName"
                      placeholder="サンプルビル1F"
                      register={register}
                      errors={errors}
                      maxLength={100}
                      requiredMark={true}
                    />
                  </div>
                  <div className="column col-lg-6 col-xl-6 col-right"></div>
                </div>
              </div>

              <div className="form-group">
                <h3 className="title-section title-md fw-bold">メール配信設定</h3>
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="column col-lg-6 col-xl-6 col-left">
                    <FormText
                      subject="差出人名"
                      name="reportMailSenderName"
                      placeholder="株式会社サンプル"
                      register={register}
                      errors={errors}
                      required={true}
                      maxLength={100}
                      requiredMark={true}
                    />
                    <p className="mt-1">レポートメールの送信元名を変更できます</p>
                  </div>
                  <div className="column col-lg-6 col-xl-6 col-right">
                    <FormText
                      subject="送信元メールアドレス"
                      name="reportMailSenderAddress"
                      placeholder="no-reply@branq.jp"
                      register={register}
                      errors={errors}
                      maxLength={100}
                      requiredMark={true}
                      readOnly={true}
                    />
                    <p className="mt-1">現在のプランでは変更できません</p>
                  </div>
                </div>
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="column col-lg-6 col-xl-6 col-left">
                    <FormText
                      subject="返信元メールアドレス"
                      name="reportMailReplyAddress"
                      placeholder="no-reply@branq.jp"
                      format="email"
                      register={register}
                      errors={errors}
                      required={true}
                      maxLength={300}
                      requiredMark={true}
                    />
                  </div>
                  <div className="column col-lg-6 col-xl-6 col-right"></div>
                </div>
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="mt-4 col-left">
                    <FormWysiwygEditor
                      subject="レポートメール末尾"
                      name="reportMailSignatureHtml"
                      placeholder="メール署名部テキスト"
                      errors={errors}
                      control={control}
                      toolbar={['link']}
                      required={true}
                      requiredMark={true}
                    />
                    <p className="mt-1">レポートメール末尾の署名部分のテキストです</p>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="column col-lg-6 col-xl-6 col-left">
                    <FormText
                      subject="プライバシーポリシーのURL"
                      name="privacyPolicyUrl"
                      placeholder="http://example.com/plivacypolicy"
                      register={register}
                      errors={errors}
                      maxLength={100}
                      requiredMark={true}
                      format="url"
                      required={true}
                    />
                    <p className="mt-1">
                      プライバシーポリシーが記載されたページのURLを記入してください
                    </p>
                  </div>
                  <div className="column col-lg-6 col-xl-6 col-right">
                    <FormText
                      subject="コピーライトの設定"
                      name="copyright"
                      placeholder="©株式会社サンプル"
                      register={register}
                      errors={errors}
                      maxLength={100}
                      required={true}
                      requiredMark={true}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <h3 className="title-section title-md fw-bold">連携</h3>
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="column col-lg-6 col-xl-6 col-left">
                    <FormText
                      subject="Benchmark API KEY"
                      name="apiKeyBenchmark"
                      placeholder="APIキー"
                      register={register}
                      errors={errors}
                      maxLength={100}
                      requiredMark={true}
                    />
                    <p className="mt-1">Benchmarkとの連携ができます</p>
                  </div>
                  <div className="column col-lg-6 col-xl-6 col-right">
                    <FormText
                      subject="Google Analytics ID"
                      name="apiKeyGoogleAnalytics"
                      placeholder="UA-XXXXX-Y"
                      register={register}
                      errors={errors}
                      maxLength={100}
                      requiredMark={true}
                    />
                    <p className="mt-1">Google Analyticsとの連携ができます</p>
                  </div>
                </div>
              </div>

              {/* アカウント未登録の場合はユーザー情報を入力する */}
              {acctState.user === null && (
                <div className="form-group">
                  <h3 className="title-section title-md fw-bold">ユーザー情報</h3>
                  <div className="row form-row flex-md-row justify-content-between">
                    <div className="column col-lg-6 col-xl-6 col-left">
                      <FormText
                        subject="氏名"
                        name="userName"
                        register={register}
                        errors={errors}
                        required={true}
                        maxLength={100}
                        requiredMark={true}
                      />
                    </div>
                    <div className="column col-lg-6 col-xl-6 col-right"></div>
                  </div>
                </div>
              )}
              <div className="form-group">
                <div className="row form-row flex-md-row justify-content-between">
                  <div className="form-footer">
                    <button
                      type="button"
                      className="btn btn-submit w-100  max-width-238"
                      onClick={onSave}
                      disabled={processing}
                    >
                      <IndicatorButton visible={processing} />
                      BranQをはじめる
                    </button>
                  </div>{' '}
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
